import type { Key9Puzzle } from '../types/puzzle';

export type FullSudokuPlacement = {
  board: string[];
  changed: boolean;
  completed: boolean;
};

export type FullNotes = string[];

export type FullNoteChange = {
  notes: FullNotes;
  changed: boolean;
};

export type FullEraseChange = {
  board: string[];
  notes: FullNotes;
  changed: boolean;
};

export function createFullBoard(puzzle: Key9Puzzle, lockedCells: Map<number, number>): string[] {
  const board = puzzle.puzzle.split('');
  lockedCells.forEach((digit, cellIndex) => {
    board[cellIndex] = String(digit);
  });
  return board;
}

export function createFullNotes(savedNotes?: string[]): FullNotes {
  const notes = Array.from({ length: 81 }, (_, index) => sanitizeNoteString(savedNotes?.[index] ?? ''));
  return notes;
}

export function placeFullDigit(
  puzzle: Key9Puzzle,
  board: string[],
  lockedCells: Set<number>,
  cellIndex: number,
  digit: number
): FullSudokuPlacement {
  if (lockedCells.has(cellIndex) || puzzle.puzzle[cellIndex] !== '0') {
    return {
      board: [...board],
      changed: false,
      completed: isFullSudokuComplete(puzzle, board)
    };
  }

  const nextBoard = [...board];
  nextBoard[cellIndex] = digit === 0 ? '0' : String(digit);

  return {
    board: nextBoard,
    changed: nextBoard[cellIndex] !== board[cellIndex],
    completed: isFullSudokuComplete(puzzle, nextBoard)
  };
}

export function toggleFullNote(
  puzzle: Key9Puzzle,
  board: string[],
  lockedCells: Set<number>,
  notes: FullNotes,
  cellIndex: number,
  digit: number
): FullNoteChange {
  if (!canEditFullCell(puzzle, board, lockedCells, cellIndex) || digit < 1 || digit > 9) {
    return { notes: [...notes], changed: false };
  }

  const nextNotes = createFullNotes(notes);
  const current = new Set(nextNotes[cellIndex].split('').filter(Boolean));
  const key = String(digit);
  if (current.has(key)) {
    current.delete(key);
  } else {
    current.add(key);
  }
  nextNotes[cellIndex] = [...current].sort().join('');
  return {
    notes: nextNotes,
    changed: nextNotes[cellIndex] !== notes[cellIndex]
  };
}

export function clearFullNotesForCell(notes: FullNotes, cellIndex: number): FullNotes {
  const nextNotes = createFullNotes(notes);
  nextNotes[cellIndex] = '';
  return nextNotes;
}

export function eraseFullCellEntry(
  puzzle: Key9Puzzle,
  board: string[],
  lockedCells: Set<number>,
  notes: FullNotes,
  cellIndex: number
): FullEraseChange {
  if (lockedCells.has(cellIndex) || puzzle.puzzle[cellIndex] !== '0') {
    return {
      board: [...board],
      notes: createFullNotes(notes),
      changed: false
    };
  }

  const nextBoard = [...board];
  const nextNotes = createFullNotes(notes);
  const changed = nextBoard[cellIndex] !== '0' || nextNotes[cellIndex] !== '';
  nextBoard[cellIndex] = '0';
  nextNotes[cellIndex] = '';
  return { board: nextBoard, notes: nextNotes, changed };
}

export function isFullSudokuComplete(puzzle: Key9Puzzle, board: string[]): boolean {
  return board.join('') === puzzle.solution;
}

function canEditFullCell(puzzle: Key9Puzzle, board: string[], lockedCells: Set<number>, cellIndex: number): boolean {
  return !lockedCells.has(cellIndex) && puzzle.puzzle[cellIndex] === '0' && board[cellIndex] === '0';
}

function sanitizeNoteString(value: string): string {
  return [...new Set(value.split('').filter((char) => /^[1-9]$/.test(char)))].sort().join('');
}
